import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Grid3x3, Loader2, Search, Trophy, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { bingoOverview, bingoClaims, reviewBingoClaim, bingoMembers, bingoTick, bingoTickMany } from "@/lib/api";

// LIFTR BINGO for staff (backend routes/bingo.py): check members' photos, tick squares the data can't see
// (events, dead hang, run club), and see who's winning. Members play at app.liftr.info/bingo or in the app.

const err = (e, fb) => e?.response?.data?.detail || fb;
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");
const TABS = [["photos", "Photos to check"], ["group", "Tick after an event"], ["tick", "Tick for one member"], ["winners", "Winners"]];

// After a run club, the Cyclathon / marathon, the plank challenge or a social: pick the square, tick everyone who came.
function GroupTick() {
  const [data, setData] = useState(null);
  const [square, setSquare] = useState("");
  const [picked, setPicked] = useState({});
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => bingoMembers("").then(setData).catch((e) => toast.error(err(e, "Couldn't load members"))), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />;
  const squares = data.squares.filter((s) => s.proof === "staff").concat(data.squares.filter((s) => s.proof !== "staff" && s.proof !== "gym"));
  const shown = data.members.filter((m) => !q.trim() || (m.name || "").toLowerCase().includes(q.trim().toLowerCase()));
  const ids = Object.keys(picked).filter((k) => picked[k]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await bingoTickMany({ task_id: square, client_ids: ids });
      toast.success(`Ticked for ${r.ticked} member${r.ticked === 1 ? "" : "s"}`);
      setPicked({}); load();
    } catch (e) { toast.error(err(e, "Couldn't save")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <select value={square} onChange={(e) => { setSquare(e.target.value); setPicked({}); }} data-testid="bingo-group-square"
        className="w-full max-w-lg bg-[#0a0a0a] border border-[#262626] rounded-md px-3 py-2 text-sm text-zinc-100">
        <option value="">Which square? (events and coach checks first)</option>
        {squares.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
      </select>
      {square && (
        <>
          <div className="flex gap-2 items-center flex-wrap">
            <div className="relative max-w-xs flex-1">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name"
                className="w-full bg-[#0a0a0a] border border-[#262626] rounded-md pl-9 pr-3 py-2 text-sm text-zinc-100" />
            </div>
            <button type="button" disabled={!ids.length || busy} onClick={save}
              className="h-9 px-4 rounded-full bg-emerald-600 hover:bg-emerald-500 text-sm text-white disabled:opacity-50" data-testid="bingo-group-save">
              {busy ? "Saving" : `Tick ${ids.length || ""} member${ids.length === 1 ? "" : "s"}`}
            </button>
          </div>
          <div className="card-surface divide-y divide-[#1f1f1f] max-h-[520px] overflow-auto">
            {shown.map((m) => {
              const done = m.done.includes(square);
              return (
                <label key={m.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${done ? "text-zinc-500" : "text-zinc-200 cursor-pointer"}`}>
                  <input type="checkbox" disabled={done} checked={done || !!picked[m.id]} onChange={(e) => setPicked({ ...picked, [m.id]: e.target.checked })} />
                  <span className="flex-1">{m.name || "No name"} <span className="text-zinc-500 text-xs">··{m.phone_end}</span></span>
                  <span className="text-xs text-zinc-500">{done ? "already ticked" : m.playing ? `${m.done.length}/20` : "not started"}</span>
                </label>
              );
            })}
            {!shown.length && <div className="p-3 text-sm text-zinc-500">No members found. Members show here once they've signed in to the app or app.liftr.info/bingo.</div>}
          </div>
        </>
      )}
    </div>
  );
}

function Photos({ allGyms, onChange }) {
  const [rows, setRows] = useState(null);
  const [notes, setNotes] = useState({});
  const load = useCallback(() => bingoClaims(allGyms).then((r) => setRows(r.claims)).catch((e) => toast.error(err(e, "Couldn't load photos"))), [allGyms]);
  useEffect(() => { load(); }, [load]);
  const review = async (c, approve) => {
    try { await reviewBingoClaim(c.id, { approve, note: notes[c.id] || null }); setRows((r) => r.filter((x) => x.id !== c.id)); onChange(); }
    catch (e) { toast.error(err(e, "Couldn't save")); }
  };
  if (!rows) return <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />;
  if (!rows.length) return <div className="card-surface p-6 text-sm text-zinc-400">No photos waiting. Members send them from the app or app.liftr.info/bingo.</div>;
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {rows.map((c) => (
        <div key={c.id} className="card-surface p-3 space-y-2" data-testid="bingo-claim">
          <div className="text-sm text-zinc-100 font-medium">{c.member_name}</div>
          <div className="text-xs text-zinc-400">{c.task_title} · sent {when(c.submitted_at)}</div>
          {c.photo_url && <a href={c.photo_url} target="_blank" rel="noreferrer"><img src={c.photo_url} alt="" className="w-full max-h-80 object-contain rounded-md bg-black" /></a>}
          {c.note && <div className="text-xs text-zinc-400">"{c.note}"</div>}
          <input value={notes[c.id] || ""} onChange={(e) => setNotes({ ...notes, [c.id]: e.target.value })} placeholder="Reason, if you reject it (the member sees this)"
            className="w-full bg-[#0a0a0a] border border-[#262626] rounded-md px-2 py-1.5 text-xs text-zinc-100" />
          <div className="flex gap-2">
            <button type="button" onClick={() => review(c, true)} className="flex-1 h-8 rounded-full bg-emerald-600 hover:bg-emerald-500 text-sm text-white inline-flex items-center justify-center gap-1"><Check className="h-4 w-4" />Accept</button>
            <button type="button" onClick={() => review(c, false)} className="flex-1 h-8 rounded-full border border-[#2a2a2a] text-sm text-zinc-300 inline-flex items-center justify-center gap-1"><X className="h-4 w-4" />Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Tick() {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(null);
  const search = useCallback(() => bingoMembers(q).then(setData).catch((e) => toast.error(err(e, "Couldn't search"))), [q]);
  useEffect(() => { const t = setTimeout(search, 300); return () => clearTimeout(t); }, [search]);
  const toggle = async (m, sq) => {
    const on = !m.done.includes(sq.id);
    try {
      await bingoTick({ client_id: m.id, task_id: sq.id, done: on });
      const upd = { ...m, playing: true, done: on ? [...m.done, sq.id] : m.done.filter((x) => x !== sq.id) };
      setPicked(upd);
      setData((d) => ({ ...d, members: d.members.map((x) => (x.id === m.id ? upd : x)) }));
    } catch (e) { toast.error(err(e, "Couldn't save")); }
  };
  return (
    <div className="space-y-3">
      <div className="relative max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Member name or phone"
          className="w-full bg-[#0a0a0a] border border-[#262626] rounded-md pl-9 pr-3 py-2 text-sm text-zinc-100" data-testid="bingo-member-search" />
      </div>
      <p className="text-xs text-zinc-500">Members show here once they've signed in to the app or app.liftr.info/bingo.</p>
      <div className="grid md:grid-cols-[260px_1fr] gap-3">
        <div className="card-surface divide-y divide-[#1f1f1f] max-h-[480px] overflow-auto">
          {(data?.members || []).map((m) => (
            <button key={m.id} type="button" onClick={() => setPicked(m)}
              className={`w-full text-left px-3 py-2 text-sm ${picked?.id === m.id ? "bg-white/5 text-white" : "text-zinc-300"}`}>
              {m.name || "No name"} <span className="text-zinc-500 text-xs">··{m.phone_end}</span>
              <span className="float-right text-xs text-zinc-500">{m.playing ? `${m.done.length}/20` : "not started"}</span>
            </button>
          ))}
          {data && !data.members.length && <div className="p-3 text-sm text-zinc-500">No one found.</div>}
        </div>
        {picked && (
          <div className="card-surface p-3">
            <div className="text-sm text-zinc-100 mb-2">{picked.name}: tap a square to tick or untick it</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {data.squares.map((sq) => {
                const on = picked.done.includes(sq.id);
                return (
                  <button key={sq.id} type="button" onClick={() => toggle(picked, sq)}
                    className={`text-left text-xs rounded-md border p-2 min-h-[64px] ${on ? "border-amber-400/60 bg-amber-400/15 text-amber-100" : "border-[#2a2a2a] text-zinc-300"}`}>
                    {sq.title}
                    {sq.proof === "staff" && <span className="block text-[10px] text-zinc-500 mt-1">coach tick</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Race = ({ title, rows, all }) => (
  <div className="card-surface p-3">
    <div className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-2 inline-flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5" />{title}</div>
    {!rows.length ? <div className="text-sm text-zinc-500">No one yet.</div> : (
      <ol className="text-sm space-y-1">
        {(all ? rows : rows.slice(0, 3)).map((r, i) => (
          <li key={r.client_id} className="flex gap-2"><span className="text-zinc-500 w-5">{i + 1}.</span><span className="flex-1 text-zinc-100">{r.name}</span>
            <span className="text-zinc-500 text-xs">{r.gym} · {when(r.at)}</span></li>
        ))}
      </ol>
    )}
  </div>
);

export default function BingoAdmin() {
  const { activeFacility, user } = useAuth();
  const isAdmin = atLeast(user?.role, "admin");
  const [tab, setTab] = useState("photos");
  const [allGyms, setAllGyms] = useState(false);
  const [ov, setOv] = useState(null);
  const load = useCallback(() => bingoOverview(allGyms).then(setOv).catch((e) => toast.error(err(e, "Couldn't load BINGO"))), [allGyms]);
  useEffect(() => { load(); }, [load, activeFacility?.id]);
  if (!activeFacility && !isAdmin) return <div className="text-sm text-zinc-400">Pick your gym first.</div>;
  return (
    <div className="space-y-5 max-w-5xl" data-testid="bingo-admin">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5"><Grid3x3 className="h-3.5 w-3.5" />Member App</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">LIFTR BINGO</h2>
          <p className="text-sm text-zinc-400 mt-1">
            {ov ? `${ov.start} to ${ov.end} · ${ov.players} playing${allGyms ? " across LIFTR" : ` at ${activeFacility?.name || "this gym"}`}` : "Loading"}.
            Members play in the app or at app.liftr.info/bingo. Most squares tick themselves; coaches check the photos and tick event squares here.
          </p>
        </div>
        <a href="/bingo?preview=1" target="_blank" rel="noreferrer" data-testid="bingo-preview"
          className="h-9 px-4 rounded-full border border-amber-400/50 text-sm text-amber-100 inline-flex items-center">See the member card</a>
        {isAdmin && (
          <label className="text-sm text-zinc-300 inline-flex items-center gap-2">
            <input type="checkbox" checked={allGyms} onChange={(e) => setAllGyms(e.target.checked)} />Every gym
          </label>
        )}
      </div>
      <div className="flex gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`h-9 px-4 rounded-full text-sm border ${tab === k ? "border-rose-500/60 bg-rose-500/10 text-white" : "border-[#2a2a2a] text-zinc-400"}`}>
            {l}{k === "photos" && ov?.pending_photos ? ` (${ov.pending_photos})` : ""}
          </button>
        ))}
      </div>
      {tab === "photos" && <Photos allGyms={allGyms} onChange={load} />}
      {tab === "group" && <GroupTick />}
      {tab === "tick" && <Tick />}
      {tab === "winners" && ov && (
        <div className="space-y-3">
          <div className="grid md:grid-cols-3 gap-3">
            <Race title="Early Five" rows={ov.early_five} />
            <Race title="First Line" rows={ov.line} />
            <Race title={`Full House (${ov.full_house.length})`} rows={ov.full_house} all />
          </div>
          <div className="card-surface p-3">
            <div className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-2">Most squares done</div>
            <div className="grid sm:grid-cols-2 gap-x-6 text-sm">
              {ov.leaderboard.map((r, i) => (
                <div key={r.client_id} className="flex gap-2 py-0.5"><span className="text-zinc-500 w-6">{i + 1}.</span><span className="flex-1 text-zinc-200">{r.name}</span>
                  <span className="text-zinc-500 text-xs">{r.gym}</span><span className="w-10 text-right tabular-nums">{r.done}/20</span></div>
              ))}
            </div>
          </div>
          <div className="card-surface p-3">
            <div className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-2">Squares: how many have done each</div>
            <div className="grid sm:grid-cols-2 gap-x-6 text-sm">
              {ov.squares.map((s) => <div key={s.id} className="flex py-0.5"><span className="flex-1 text-zinc-300">{s.title}</span><span className="tabular-nums text-zinc-400">{s.done_by}</span></div>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
